import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, Shield, Search, RefreshCw, ChevronLeft, Clock, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabase';

const AdminPanel = ({ t }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [meId, setMeId] = useState(null);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, name, email, approved, role, created_at')
                .order('created_at', { ascending: false });
            if (error) throw error;
            setUsers(data || []);
        } catch (error) {
            console.error('Error fetching users:', error.message || error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => setMeId(data.user?.id || null));
        fetchUsers();
    }, []);

    const handleToggleApproval = async (userId, currentStatus) => {
        setActionLoading(userId);
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ approved: !currentStatus })
                .eq('id', userId);
            if (error) throw error;
            setUsers(users.map(u => u.id === userId ? { ...u, approved: !currentStatus } : u));
        } catch (error) {
            console.error('Error updating approval:', error.message || error);
            alert('Erro ao atualizar aprovação. Verifique se você tem permissão de Administrador.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`Tem certeza que deseja excluir permanentemente o usuário "${userName || 'Sem nome'}"? Esta ação não pode ser desfeita e todos os dados vinculados (produtos, canais, etc.) serão removidos.`)) {
            return;
        }

        setActionLoading(userId);
        try {
            // RPC com security definer: apaga auth.users -> cascade remove profile + dados
            const { error } = await supabase.rpc('admin_delete_user', { target: userId });
            if (error) throw error;
            setUsers(users.filter(u => u.id !== userId));
            alert(`Usuário "${userName || 'Sem nome'}" e seus dados foram excluídos com sucesso.`);
        } catch (error) {
            console.error('Error deleting user:', error.message || error);
            alert('Erro ao excluir usuário. Verifique sua conexão ou se você tem permissão de Administrador.');
        } finally {
            setActionLoading(null);
        }
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Shield size={20} color={t.ink} />
                    <h2 style={{ fontFamily: t.disp, fontSize: 19, fontWeight: 700, color: t.text, margin: 0, letterSpacing: '-0.01em' }}>Gestão de Acessos</h2>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} color={t.textMuted} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                            type="text"
                            placeholder="Buscar usuários..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '9px 12px 9px 34px',
                                borderRadius: 0,
                                border: `1.5px solid ${t.ink}`,
                                background: t.bgInput,
                                color: t.text,
                                fontFamily: t.mono,
                                fontSize: 12,
                                width: 240
                            }}
                        />
                    </div>
                    <button
                        onClick={fetchUsers}
                        style={{
                            padding: '9px 14px', borderRadius: 0,
                            border: `1.5px solid ${t.ink}`, background: t.bgCard,
                            color: t.text, cursor: 'pointer', fontFamily: t.mono, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase',
                            display: 'flex', alignItems: 'center', gap: 8
                        }}
                        disabled={loading}
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        Atualizar
                    </button>
                </div>
            </div>

            <div style={{ background: t.bgCard, borderRadius: 0, border: `1.5px solid ${t.ink}`, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: t.bgInput, borderBottom: `1.5px solid ${t.ink}` }}>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Usuário</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                            <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</th>
                            <th style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 600, color: t.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && users.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>Carregando base de usuários...</td>
                            </tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontStyle: 'italic' }}>Nenhum usuário encontrado.</td>
                            </tr>
                        ) : (
                            filteredUsers.map((user) => (
                                <tr key={user.id} style={{ borderBottom: `1px solid ${t.border}`, transition: 'background 0.2s' }}>
                                    <td style={{ padding: '12px 20px' }}>
                                        <div style={{ fontWeight: 500, color: t.text }}>{user.name || 'Sem nome'}</div>
                                        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{user.email}</div>
                                    </td>
                                    <td style={{ padding: '12px 20px' }}>
                                        {user.approved ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                padding: '2px 8px', borderRadius: 4,
                                                background: `${t.success}15`, color: t.success,
                                                fontSize: 11, fontWeight: 600, textTransform: 'uppercase'
                                            }}>
                                                <CheckCircle size={12} /> Aprovado
                                            </span>
                                        ) : (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                padding: '2px 8px', borderRadius: 4,
                                                background: `${t.danger}15`, color: t.danger,
                                                fontSize: 11, fontWeight: 600, textTransform: 'uppercase'
                                            }}>
                                                <Clock size={12} /> Pendente
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px 20px' }}>
                                        <span style={{
                                            fontSize: 12,
                                            color: user.role === 'admin' ? t.accent : t.textSecondary,
                                            fontWeight: user.role === 'admin' ? 600 : 400
                                        }}>
                                            {user.role === 'admin' ? 'Administrador' : 'Operador'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                            {user.id !== meId ? (
                                                <>
                                                    <button
                                                        onClick={() => handleToggleApproval(user.id, user.approved)}
                                                        disabled={actionLoading === user.id}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 6,
                                                            border: `1px solid ${user.approved ? t.danger + '40' : t.success + '40'}`,
                                                            background: 'transparent',
                                                            color: user.approved ? t.danger : t.success,
                                                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        {actionLoading === user.id ? '...' : user.approved ? 'Revogar' : 'Aprovar'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user.id, user.name)}
                                                        disabled={actionLoading === user.id}
                                                        title="Excluir Usuário"
                                                        style={{
                                                            width: 30, height: 30, borderRadius: 6,
                                                            border: `1px solid ${t.border}`, background: 'transparent',
                                                            color: t.danger, cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = `${t.danger}10`}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </>
                                            ) : (
                                                <span style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic', padding: '6px 12px' }}>Você</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 24 }}>
                <div style={{ background: t.bgCard, padding: 16, borderRadius: 12, border: `1px solid ${t.border}`, flex: 1 }}>
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Total Geral</p>
                    <p style={{ fontSize: 24, fontWeight: 700, color: t.text, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{users.length}</p>
                </div>
                <div style={{ background: t.bgCard, padding: 16, borderRadius: 12, border: `1px solid ${t.border}`, flex: 1 }}>
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Aguardando Aprovação</p>
                    <p style={{ fontSize: 24, fontWeight: 700, color: t.danger, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{users.filter(u => !u.approved).length}</p>
                </div>
                <div style={{ background: t.bgCard, padding: 16, borderRadius: 12, border: `1px solid ${t.border}`, flex: 1 }}>
                    <p style={{ fontSize: 11, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>Administradores</p>
                    <p style={{ fontSize: 24, fontWeight: 700, color: t.accent, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{users.filter(u => u.role === 'admin').length}</p>
                </div>
            </div>
        </div>
    );
};

export default AdminPanel;
